import * as React from 'react';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS } from '@/components/ui/recipes';

interface ListRowProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** A glyph before the label, drawn h-4 w-4 in the hint colour. */
  icon?: React.ReactNode;
  label: React.ReactNode;
  /** A line under the label; on a card it wraps to a second line. */
  description?: React.ReactNode;
  /** A small faint line under the description (a lesson's step count). */
  meta?: React.ReactNode;
  /** What sits at the row's end: a count, a shortcut, a status. */
  trailing?: React.ReactNode;
  selected?: boolean;
  /** A row that deletes or ends something. */
  destructive?: boolean;
  /** plain: a row in a list or menu; card: a choice on its own card, its glyph in a tinted circle (a new anchor's kind, a tutorial); lead: a card outlined in the brand ink that opens somewhere bigger (a library's store); outline: an item of an editor panel's list on its own card outlined in the secondary, its glyph plain (an animation, a keyframe). */
  look?: 'plain' | 'card' | 'lead' | 'outline';
}

/** Lureo's list row: one item in a list a panel or menu shows, which does something when clicked. */
export const ListRow = React.forwardRef<HTMLButtonElement, ListRowProps>(
  ({ icon, label, description, meta, trailing, selected = false, destructive = false, look = 'plain', className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-pressed={selected || undefined}
      className={cn(
        'flex w-full items-center rounded-lureo-radius text-left font-lureo-font-ui transition-colors',
        look === 'card' && 'gap-3 border-lureo-border-width border-lureo-line bg-lureo-surface px-3.5 py-2.5 text-lureo-14 shadow-lureo-shadow-control hover:border-lureo-brand-ink',
        look === 'lead' && 'gap-3 rounded-lureo-radius-lg border-lureo-border-width-medium border-lureo-brand-ink bg-lureo-surface px-4 py-3 text-lureo-15 hover:bg-lureo-brand-ink/[0.06]',
        look === 'plain' && 'gap-2.5 px-2.5 py-1.5 text-lureo-13',
        look === 'outline' && 'gap-2 rounded-lureo-radius-lg border-lureo-border-width-medium border-lureo-secondary bg-lureo-surface p-3 text-lureo-14 transition-all duration-lureo-duration-slow hover:bg-lureo-secondary/10 hover:shadow-lureo-shadow-raised active:bg-lureo-secondary/20',
        FOCUS,
        DISABLED,
        selected ? 'bg-lureo-brand-ink/10 text-lureo-brand-ink' : destructive ? 'text-lureo-danger-ink hover:bg-lureo-danger/[0.08]' : cn('text-lureo-ink', look === 'plain' && 'hover:bg-lureo-hover'),
        className,
      )}
      {...props}
    >
      {icon &&
        (look === 'card' || look === 'lead' ? (
          <span className={cn('grid shrink-0 place-items-center rounded-full bg-lureo-brand-ink/10 text-lureo-brand-ink', look === 'lead' ? 'h-9 w-9 [&_svg]:h-5 [&_svg]:w-5' : 'h-8 w-8 [&_svg]:h-4.5 [&_svg]:w-4.5')}>{icon}</span>
        ) : (
          <span className={cn('flex shrink-0 items-center', look === 'outline' ? '[&_svg]:h-3.5 [&_svg]:w-3.5' : '[&_svg]:h-4 [&_svg]:w-4', selected || destructive || look === 'outline' ? 'text-current' : 'text-lureo-ink-subtle')}>{icon}</span>
        ))}
      <span className={cn('flex min-w-0 flex-1 flex-col', (look === 'card' || look === 'lead') && 'gap-px')}>
        <span className={cn('truncate', look === 'card' || look === 'lead' ? 'leading-tight' : 'leading-[1.3]', look !== 'plain' && 'font-medium', look === 'lead' && 'text-lureo-brand-ink', look === 'outline' && 'leading-5')}>{label}</span>
        {description && <span className={cn(look === 'card' ? 'line-clamp-2' : 'truncate', 'font-lureo-font-prose text-lureo-ink-subtle', look === 'card' || look === 'lead' ? 'leading-tight' : 'leading-[1.4]', look === 'plain' ? 'text-lureo-12' : 'text-lureo-13', look === 'outline' && 'font-lureo-font-ui text-lureo-12 font-semibold leading-4')}>{description}</span>}
        {meta && <span className="mt-0.5 font-lureo-font-prose text-lureo-12 leading-tight text-lureo-ink-subtlest">{meta}</span>}
      </span>
      {trailing && <span className={cn('flex shrink-0 items-center gap-1.5 text-lureo-12', look === 'lead' ? 'text-lureo-brand-ink' : 'text-lureo-ink-subtlest')}>{trailing}</span>}
    </button>
  ),
);
ListRow.displayName = 'ListRow';
